import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const ContactPage = () => {
  const [searchParams] = useSearchParams();

  const getInitialState = () => {
    const purpose = searchParams.get('purpose');
    if (purpose === 'partner') {
      return { step: 3, userType: 'sr', purposeType: 'partner', inquiry: 'パートナー事務所登録について詳細を教えてください。' };
    }
    return { step: 1, userType: '', purposeType: '', inquiry: '' };
  };

  const initial = getInitialState();
  const [currentStep, setCurrentStep] = useState(initial.step);
  const [userType, setUserType] = useState(initial.userType);
  const [purposeType, setPurposeType] = useState(initial.purposeType);
  const [formData, setFormData] = useState(initial.inquiry ? { inquiry: initial.inquiry } : {});
  const [openFaq, setOpenFaq] = useState(null);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    fetch('/api/admin-read.php?file=downloads&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setDownloads)
      .catch(() => setDownloads([]));
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const srFaqs = [
    {
      question: '導入までどのくらいの期間がかかりますか？',
      answer: '必要事項をご送付いただき、登録完了後、1〜2営業日以内にご利用を開始いただけます。'
    },
    {
      question: '既存の勤怠システムからの移行は可能ですか？',
      answer: '既存システムからのデータ移行には対応しておりません。SrWithの利用開始日以降のデータから対応いたします。'
    },
    {
      question: '契約期間の縛りはありますか？',
      answer: '最低契約期間は特にありません。'
    },
    {
      question: 'セキュリティ対策はどうなっていますか？',
      answer: 'SSL暗号化通信、データの定期バックアップ、アクセス権限管理など、万全のセキュリティ対策を施しております。個人情報保護法にも準拠しています。'
    },
    {
      question: '無料トライアルはありますか？',
      answer: 'ご希望の方はSrWith事務局までお問い合わせください。'
    },
    {
      question: 'サポート体制はありますか？',
      answer: 'SrWithが継続的にサポートいたします。システムの使い方についていつでもお問い合わせいただけます。'
    }
  ];

  const clientFaqs = [
    {
      question: '導入までどのくらいの期間がかかりますか？',
      answer: '顧問社労士によって異なります。詳しくは担当の社労士にご相談ください。'
    },
    {
      question: '顧問社労士との契約は必要ですか？',
      answer: 'はい、SrWithは社労士連携型のシステムのため、顧問社労士との契約が必要となります。'
    },
    {
      question: '既存の勤怠システムからの移行は可能ですか？',
      answer: '既存システムからのデータ移行には対応しておりません。SrWithの利用開始日以降のデータから対応いたします。'
    },
    {
      question: '従業員が増えた場合はどうなりますか？',
      answer: '顧問社労士との契約内容によりますので、顧問社労士にお尋ねください。'
    },
    {
      question: '契約期間の縛りはありますか？',
      answer: '最低契約期間は特にありません。顧問社労士にご相談ください。'
    },
    {
      question: 'セキュリティ対策はどうなっていますか？',
      answer: 'SSL暗号化通信、データの定期バックアップ、アクセス権限管理など、万全のセキュリティ対策を施しております。個人情報保護法にも準拠しています。'
    },
    {
      question: '無料トライアルはありますか？',
      answer: '顧問社労士にご相談ください。'
    },
    {
      question: 'サポート体制について教えてください',
      answer: '導入後も顧問社労士による継続的なサポートを提供いたします。'
    }
  ];

  const faqs = userType === 'sr' ? srFaqs : clientFaqs;

  // 入力値更新
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ステップ1: ユーザー選択
  const handleUserSelect = (type) => {
    setUserType(type);
    setCurrentStep(2);
  };

  // ステップ2: 目的選択
  const handlePurposeSelect = (purpose) => {
    setPurposeType(purpose);
    
    // 定型文を設定
    const defaultMessages = {
      // 社労士向け
      'partner': 'パートナー事務所登録について詳細を教えてください。',
      'service': 'SrWithのサービス詳細について説明をお聞きしたいです。',
      'consultation': '業界の課題や協業について相談させてください。',
      'other': 'ご相談があります。',
      
      // 企業向け  
      'document': 'SrWithの資料をお送りください。',
      'service_client': 'SrWithのサービス詳細について説明をお聞きしたいです。',
      'other_client': 'ご相談があります。'
    };
    
    setFormData(prev => ({ 
      ...prev, 
      inquiry: defaultMessages[purpose] || '' 
    }));
    
    setCurrentStep(3);
  };

  // ステップ3: フォーム送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userType, purposeType, ...formData }),
      });
      const data = await response.json();

      if (data.success) {
        setSubmitResult('success');
        // 資料請求の場合、自動ダウンロード
        if (purposeType === 'document') {
          const doc = downloads.find((d) => d.tab === 'client');
          if (doc?.fileUrl) {
            const a = document.createElement('a');
            a.href = doc.fileUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        }
      } else {
        setSubmitResult('error');
      }
    } catch (err) {
      setSubmitResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ← 戻るボタン
  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setUserType('');
    } else if (currentStep === 3) {
      setCurrentStep(2);
      setPurposeType('');
      setFormData({});
    }
  };

  // 社労士向け目的選択肢
  const srPurposes = [
    {
      id: 'partner',
      title: 'パートナー事務所登録について',
      desc: 'SrWithパートナーとして業務提携をご検討の方'
    },
    {
      id: 'service',
      title: 'サービス詳細について',
      desc: 'SrWithの機能や導入について詳しく知りたい方'
    },
    {
      id: 'consultation',
      title: '業界情報・相談について',
      desc: '業界の課題や協業について相談したい方'
    },
    {
      id: 'other',
      title: 'その他のご相談',
      desc: '上記以外のご相談がある方'
    }
  ];

  // 企業向け目的選択肢
  const clientPurposes = [
    {
      id: 'document',
      title: '資料請求',
      desc: 'まずは情報だけ欲しい方・比較検討中の方'
    },
    {
      id: 'service_client',
      title: 'サービス詳細説明をお聞きしたい',
      desc: '具体的な機能や効果について詳しく知りたい方'
    },
    {
      id: 'other_client',
      title: 'その他のご相談',
      desc: '上記以外のご相談がある方'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="お問い合わせ｜無料相談受付中"
        description="SrWithへのお問い合わせ。社労士の方はパートナー登録、企業様は資料請求が可能。よくある質問もご確認いただけます。"
      />

      {/* Hero Section - 写真オーバーレイ */}
      <div className="relative min-h-[60vh] md:min-h-screen flex items-center">
        {/* 背景写真エリア */}
        <div className="absolute inset-0 bg-gray-200">
          <img
            src={`${process.env.PUBLIC_URL}/images/wpose.jpg`}
            alt="SrWith チームメンバー"
            className="w-full h-full object-cover object-[center_60%]"
          />
        </div>

        {/* グラデーションオーバーレイ */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>

        {/* コンテンツオーバーレイ */}
        <div className="absolute bottom-12 md:bottom-24 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center">
              <button className="bg-pink-beige hover:bg-pink-dark text-white font-bold text-base md:text-xl px-8 md:px-16 py-3 md:py-5 rounded-full shadow-2xl transition-all duration-300 hover:scale-105">
                まずはお気軽にご相談ください
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* メインコンテンツエリア */}
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          
          {/* プログレスバー */}
          <div className="flex items-center justify-center mb-12 md:mb-16">
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base ${currentStep >= 1 ? 'bg-pink-beige' : 'bg-gray-300'}`}>
                1
              </div>
              <div className={`w-12 md:w-16 h-1 ${currentStep >= 2 ? 'bg-pink-beige' : 'bg-gray-300'}`}></div>
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base ${currentStep >= 2 ? 'bg-pink-beige' : 'bg-gray-300'}`}>
                2
              </div>
              <div className={`w-12 md:w-16 h-1 ${currentStep >= 3 ? 'bg-pink-beige' : 'bg-gray-300'}`}></div>
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base ${currentStep >= 3 ? 'bg-pink-beige' : 'bg-gray-300'}`}>
                3
              </div>
            </div>
          </div>

          {/* STEP 1: ユーザー選択 */}
          {currentStep === 1 && (
            <div className="text-center space-y-8 md:space-y-12 animate-fade-in pb-32 md:pb-0">
              <h2 className="text-2xl md:text-4xl font-bold text-brown">
                どちらに該当しますか？
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
                <button
                  onClick={() => handleUserSelect('sr')}
                  className="group bg-white p-8 md:p-12 rounded-2xl md:rounded-3xl shadow-xl border hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <h3 className="text-2xl md:text-3xl font-bold text-brown mb-3 md:mb-4">社労士の方</h3>
                  <div className="space-y-2 md:space-y-3 text-gray-text">
                    <p>・パートナー登録</p>
                    <p>・業界情報交換</p>
                    <p>・サービス詳細</p>
                  </div>
                  <div className="mt-6 md:mt-8">
                    <span className="inline-block bg-pink-beige text-white px-6 md:px-8 py-2 md:py-3 rounded-full font-semibold group-hover:bg-brown transition-colors duration-300">
                      こちらを選択
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => handleUserSelect('client')}
                  className="group bg-white p-8 md:p-12 rounded-2xl md:rounded-3xl shadow-xl border hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <h3 className="text-2xl md:text-3xl font-bold text-brown mb-3 md:mb-4">企業・事業主の方</h3>
                  <div className="space-y-2 md:space-y-3 text-gray-text">
                    <p>・労務管理効率化</p>
                    <p>・法令遵守支援</p>
                    <p>・システム導入</p>
                  </div>
                  <div className="mt-6 md:mt-8">
                    <span className="inline-block bg-pink-beige text-white px-6 md:px-8 py-2 md:py-3 rounded-full font-semibold group-hover:bg-brown transition-colors duration-300">
                      こちらを選択
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: 目的選択 */}
          {currentStep === 2 && (
            <div className="text-center space-y-8 md:space-y-12 animate-fade-in">
              <div className="text-center space-y-4 md:space-y-6">
                <h2 className="text-2xl md:text-4xl font-bold text-brown">
                  {userType === 'sr' ? '社労士の皆様へ' : '企業・事業主の皆様へ'}
                </h2>
                <p className="text-lg md:text-xl text-gray-text px-4">
                  {userType === 'sr' 
                    ? '同業者として、業界の発展を共に目指しましょう'
                    : '社労士と一緒に、より良い労務管理を実現しませんか？'
                  }
                </p>
                <h3 className="text-xl md:text-2xl font-semibold text-brown">
                  {userType === 'sr' ? 'どのようなご相談でしょうか？' : 'まずはどちらから始めますか？'}
                </h3>
              </div>

              <div className="space-y-3 md:space-y-4 max-w-3xl mx-auto">
                {(userType === 'sr' ? srPurposes : clientPurposes).map((purpose) => (
                  <button
                    key={purpose.id}
                    onClick={() => handlePurposeSelect(purpose.id)}
                    className="w-full text-center bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border hover:shadow-xl hover:border-pink-beige transition-all duration-300"
                  >
                    <h4 className="text-lg md:text-xl font-semibold text-brown mb-1 md:mb-2">{purpose.title}</h4>
                    <p className="text-gray-text text-sm md:text-base">{purpose.desc}</p>
                  </button>
                ))}
              </div>

              <div className="text-center">
                <button
                  onClick={handleBack}
                  className="px-6 md:px-8 py-2 md:py-3 bg-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-300 transition-colors duration-300"
                >
                  ← 戻る
                </button>
              </div>
            </div>
          )}

          {/* 送信完了モーダル */}
          {submitResult === 'success' && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="fixed inset-0 bg-black bg-opacity-50"></div>
              <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl md:rounded-3xl max-w-lg w-full p-8 md:p-12 shadow-2xl text-center">
                  <div className="text-5xl mb-6">&#9993;</div>
                  <h2 className="text-2xl md:text-3xl font-bold text-brown mb-4">
                    お問い合わせありがとうございます
                  </h2>
                  <p className="text-gray-text text-lg leading-relaxed mb-8">
                    ご入力いただいたメールアドレスに確認メールをお送りしました。<br />
                    内容を確認の上、1〜2営業日以内に担当者よりご連絡いたします。
                  </p>
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      setUserType('');
                      setPurposeType('');
                      setFormData({});
                      setAgreedToPrivacy(false);
                      setSubmitResult(null);
                    }}
                    className="px-8 py-3 bg-pink-beige text-white rounded-full font-semibold hover:bg-brown transition-colors duration-300"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: フォーム */}
          {currentStep === 3 && (
            <div className="text-center space-y-8 md:space-y-12 animate-fade-in">
              <div className="text-center space-y-4 md:space-y-6">
                <h2 className="text-2xl md:text-4xl font-bold text-brown px-4">
                  {srPurposes.concat(clientPurposes).find(p => p.id === purposeType)?.title}
                </h2>
                <p className="text-lg md:text-xl text-gray-text px-4">
                  {purposeType === 'partner' && 'まずは情報交換から始めさせていただきます'}
                  {purposeType === 'service_client' && '詳しい機能説明をいたします'}
                  {purposeType === 'service' && '同業者として詳しくご説明いたします'}
                  {purposeType === 'consultation' && '業界の発展について一緒に考えましょう'}
                  {(purposeType === 'other' || purposeType === 'other_client') && 'どんなことでもお気軽にご相談ください'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-white p-6 md:p-10 rounded-2xl md:rounded-3xl shadow-xl">
                <div className="space-y-4 md:space-y-6">
                  {/* ハニーポット（ボット対策・非表示） */}
                  <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <input
                      type="text"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                      onChange={(e) => handleInputChange('website_url', e.target.value)}
                    />
                  </div>

                  {/* 基本項目 */}
                  <div>
                    <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">お名前 *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                      placeholder="例: 田中太郎"
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">メールアドレス *</label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                      placeholder="例: info@company.co.jp"
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </div>

                  {/* 資料請求の場合は最小限 */}
                  {purposeType === 'document' && (
                    <div>
                      <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">会社名（任意）</label>
                      <input
                        type="text"
                        className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                      />
                    </div>
                  )}

                  {/* パートナー登録の場合 */}
                  {userType === 'sr' && purposeType === 'partner' && (
                    <>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">事務所名 *</label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('officeName', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">所在地 *</label>
                        <select
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('prefecture', e.target.value)}
                        >
                          <option value="">都道府県を選択</option>
                          <option value="佐賀県">佐賀県</option>
                          <option value="福岡県">福岡県</option>
                          <option value="長崎県">長崎県</option>
                          <option value="その他">その他</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* サービス詳細の場合 */}
                  {purposeType === 'service_client' && userType === 'client' && (
                    <>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">会社名 *</label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('companyName', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">電話番号 *</label>
                        <input
                          type="tel"
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          placeholder="例: 090-1234-5678"
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">従業員数</label>
                        <select
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                        >
                          <option value="">選択してください</option>
                          <option value="1-10名">1-10名</option>
                          <option value="11-30名">11-30名</option>
                          <option value="31-50名">31-50名</option>
                          <option value="51-100名">51-100名</option>
                          <option value="101名以上">101名以上</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* その他の社労士向け項目 */}
                  {userType === 'sr' && (purposeType === 'service' || purposeType === 'consultation' || purposeType === 'other') && (
                    <>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">事務所名 *</label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('officeName', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">電話番号（任意）</label>
                        <input
                          type="tel"
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          placeholder="例: 090-1234-5678"
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {/* その他の企業向け項目 */}
                  {userType === 'client' && purposeType === 'labor' && (
                    <>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">会社名 *</label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('companyName', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">従業員数</label>
                        <select
                          className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                          onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                        >
                          <option value="">選択してください</option>
                          <option value="1-10名">1-10名</option>
                          <option value="11-30名">11-30名</option>
                          <option value="31-50名">31-50名</option>
                          <option value="51-100名">51-100名</option>
                          <option value="101名以上">101名以上</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-brown font-semibold mb-2 md:mb-3 text-left">
                      {purposeType === 'document' 
                        ? 'ご要望（任意）' 
                        : 'お問い合わせ内容'}
                    </label>
                    <textarea
                      rows="5"
                      value={formData.inquiry || ''}
                      className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-pink-beige focus:outline-none transition-colors duration-300"
                      placeholder="お気軽にご記入ください"
                      onChange={(e) => handleInputChange('inquiry', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 mt-6">
                  <input
                    type="checkbox"
                    id="privacyAgreement"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1 w-5 h-5 text-pink-beige border-gray-300 rounded focus:ring-pink-beige cursor-pointer"
                  />
                  <label htmlFor="privacyAgreement" className="text-sm text-gray-text cursor-pointer">
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-pink-beige hover:underline font-semibold"
                    >
                      プライバシーポリシー
                    </button>
                    に同意する
                  </label>
                </div>

                {submitResult === 'error' && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    送信に失敗しました。時間をおいて再度お試しください。
                  </div>
                )}

                <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 mt-8 md:mt-10">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={isSubmitting}
                    className="order-2 md:order-1 px-6 md:px-8 py-2 md:py-3 bg-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-300 transition-colors duration-300"
                  >
                    ← 戻る
                  </button>

                  <button
                    type="submit"
                    disabled={!agreedToPrivacy || isSubmitting}
                    className={`order-1 md:order-2 w-full md:w-auto px-8 md:px-12 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-all duration-300 shadow-lg ${
                      agreedToPrivacy && !isSubmitting
                        ? 'bg-pink-beige text-white hover:bg-brown hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* 料金セクション */}
      {userType === 'sr' ? (
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">料金プラン</h2>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-700 text-white">
                  <th className="px-6 py-4 text-left text-sm font-bold">登録ID数</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">月額料金（税抜）</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm text-gray-700">100IDまで</td>
                  <td className="px-6 py-4 text-sm text-gray-700">19,800円（固定）</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm text-gray-700">101ID以降</td>
                  <td className="px-6 py-4 text-sm text-gray-700">19,800円 ＋ 超過1IDにつき100円／月</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">料金について</h2>
            <p className="text-lg text-gray-text leading-relaxed">
              料金は企業様の状況により異なります。<br />
              詳しくは顧問社労士、またはSrWithに直接お問い合わせください。
            </p>
          </div>
        </section>
      )}

      {/* FAQセクション */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
              よくある質問
            </h2>
            <p className="text-lg md:text-xl text-gray-text">
              お客様からよくいただくご質問にお答えします
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 md:px-8 py-5 md:py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="text-base md:text-lg font-semibold text-brown pr-4">
                    {faq.question}
                  </span>
                  <span className={`text-pink-beige text-2xl transform transition-transform duration-300 flex-shrink-0 ${openFaq === index ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 md:px-8 pb-5 md:pb-6 text-gray-text leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* プライバシーポリシーモーダル */}
      {showPrivacyModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowPrivacyModal(false)}></div>
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* モーダルヘッダー */}
              <div className="sticky top-0 bg-pink-beige px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl md:text-2xl font-bold text-white">プライバシーポリシー</h2>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-white hover:text-gray-200 text-3xl font-light"
                >
                  ×
                </button>
              </div>

              {/* モーダルコンテンツ */}
              <div className="px-6 py-8">
                <p className="text-gray-text text-sm mb-8">最終改定日：2026年1月6日</p>

                <div className="prose prose-lg max-w-none text-gray-text leading-relaxed">
                  <p className="mb-8">
                    SrWith（以下「当社」）は、労務管理および業務支援に関するサービスを提供するにあたり、個人情報の重要性を十分に認識し、個人情報の保護に関する法令および関連ガイドラインを遵守するとともに、以下のとおりプライバシーポリシー（以下「本ポリシー」）を定め、個人情報の適切な取扱いと保護に努めます。
                  </p>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">1．適用範囲</h3>
                    <p>本ポリシーは、当社が提供する労務DXプラットフォーム「SrWith」およびこれに付随する各種サービス（以下「本サービス」）において、当社が取得・利用・管理する個人情報の取扱いについて定めるものです。</p>
                  </section>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">2．取得する情報の種類</h3>
                    <p className="mb-3">当社は、本サービスの提供にあたり、以下の情報を取得する場合があります。</p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>氏名、会社名、部署名、役職</li>
                      <li>メールアドレス、電話番号</li>
                      <li>アカウント情報（ID、パスワード等）</li>
                      <li>勤怠情報、労務管理に関する情報</li>
                      <li>アクセスログ、操作履歴、IPアドレス、ブラウザ情報</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">3．個人情報の利用目的</h3>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>本サービスの提供、運用、保守</li>
                      <li>ユーザー認証および本人確認</li>
                      <li>利用に関する連絡、通知、問い合わせ対応</li>
                      <li>サービス改善、機能開発、利用状況分析</li>
                      <li>不正利用防止およびセキュリティ確保</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">4．第三者提供</h3>
                    <p>当社は、本人の同意がある場合、法令に基づく場合等を除き、個人情報を第三者に提供しません。</p>
                  </section>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">5．安全管理措置</h3>
                    <p>当社は、個人情報への不正アクセス、漏えい、滅失または毀損の防止のため、組織的・技術的・物理的な安全管理措置を講じます。</p>
                  </section>

                  <section className="mb-8">
                    <h3 className="text-lg font-bold text-brown mb-3">6．お問い合わせ窓口</h3>
                    <div className="bg-gray-50 p-4 rounded-xl text-sm">
                      <p className="font-semibold text-brown">SrWith株式会社</p>
                      <p>佐賀県唐津市和多田海士町5-14</p>
                      <p>代表取締役　塚本　祥太郎</p>
                    </div>
                  </section>
                </div>
              </div>

              {/* モーダルフッター */}
              <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-center">
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="px-8 py-3 bg-pink-beige text-white rounded-full font-semibold hover:bg-brown transition-colors duration-300"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ContactPage;